import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { Provider } from 'react-redux';
import { init } from '@sentry/react';
import 'react-toastify/dist/ReactToastify.css';
import { App } from './App';
import configureStore from './store/configureStore';
import { waitForGlobalConfig } from './utilities/globalConfig';
import isTesting from './utilities/isTesting';
import { createCookie } from 'react-router';
import { initI18n } from './i18n';

// Removed deprecated toast.configure() - now using ToastContainer component

export let store;

// Unused - commented out for future use
// const globalConfigCookie = createCookie('global-config', {
//   maxAge: 60 * 60 * 24 * 7
// });

const originCookie = createCookie('origin', {
  maxAge: 60 * 60 * 24 * 7,
});

// Changed from async IIFE to promise chain to avoid SWC plugin issues
waitForGlobalConfig().then(async globalConfig => {
  // Set cookies using the same serialization as student-apply

  store = configureStore();
  if (globalConfig) {
    // Wait for i18next to load translations before first render so components
    // never flash untranslated keys. Reads `languages` + `defaultLanguage` off
    // globalConfig; falls back to English-only when the uni hasn't opted in.
    await initI18n(globalConfig);

    if (isTesting) {
      // @ts-ignore - Expose store for Cypress testing
      window.__store__ = store;
      // Skip config override when running on mock server port (3003)
      // envconfig.js already has the correct mock server config
      // Check for :3003 to handle both localhost:3003 and 127.0.0.1:3003
      const isMocks = window.location.host.includes(':3003');
      // @ts-ignore - Property 'globalConfig' does not exist on type 'Window & typeof globalThis'
      window.globalConfig = {
        apiUrl: isMocks ? 'http://localhost:3001/prod' : 'https://dev-api.cas-staff.net',
        assetsUrl: 'https://dev-customer-artifacts.s3.eu-west-2.amazonaws.com',
        cognitoDomain: '',
        cognitoProviderName: '',
        idpLogout: false,
        myInterviewAPIKey:
          'apikey 8bz12e7yfpw5pokqhalrzyne sig=8aed5921084046ed95eb042f44edfe1582cc1436f75b559ca16d7dac89ddb62d',
        redirectUrl: '',
        samlEnabled: false,
        staffAppClient: '4l39bkg44pjprbml4t2vh80olm',
        staffMFAEnabled: false,
        studentAppClient: '4l39bkg44pjprbml4t2vh80olm',
        studentsMFAEnabled: false,
        uni: isMocks ? 'DEMO' : 'TST',
        wssUrl: isMocks ? 'ws://localhost:3002' : 'wss://ul3yqz8boh.execute-api.eu-west-2.amazonaws.com/prod',
        userpool: 'eu-west-2_wRF3fgulK',
      };
    } else if (
      !(
        window.location.hostname.endsWith('.net') ||
        window.location.hostname.includes('localhost') ||
        window.location.hostname.includes('test-') ||
        window.location.hostname.includes('-int')
      )
    ) {
      init({
        environment: 'Production',
        dsn: 'https://d2284fb40b2f42a4953956e8d0723091@o1031975.ingest.sentry.io/5998906',
        ignoreErrors: [
          // Random plugins/extensions
          'top.GLOBALS',
          // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
          'originalCreateNotification',
          'canvas.contentDocument',
          'MyApp_RemoveAllHighlights',
          'http://tt.epicplay.com',
          "Can't find variable: ZiteReader",
          'jigsaw is not defined',
          'ComboSearch is not defined',
          'http://loading.retry.widdit.com/',
          'atomicFindClose',
          // Facebook borked
          'fb_xd_fragment',
          // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to
          // reduce this. (thanks @acdha)
          // See http://stackoverflow.com/questions/4113268
          'bmi_SafeAddOnload',
          'EBCallBackMessageReceived',
          // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
          'conduitPage',
          // Generic tawk/twk error pattern
          /twk|tawk/i,
          'googletag',
          "undefined is not an object (evaluating 'a.L')",
          'AxiosError: Network Error',
        ],
        denyUrls: [
          // Facebook flakiness
          /graph\.facebook\.com/i,
          // Facebook blocked
          /connect\.facebook\.net\/en_US\/all\.js/i,
          // Woopra flakiness
          /eatdifferent\.com\.woopra-ns\.com/i,
          /static\.woopra\.com\/js\/woopra\.js/i,
          // Chrome extensions
          /extensions\//i,
          /^chrome:\/\//i,
          /^chrome-extension:\/\//i,
          // Other plugins
          /127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
          /webappstoolbarba\.texthelp\.com\//i,
          /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
          /googletagmanager\.com/i,
          /translate\.googleapis\.com/i,
          // Generic tawk.to URL pattern
          /^(?:https?:)?\/\/.*\.?tawk\.to/,
        ],
        integrations: [
          // new Sentry.BrowserTracing({
          //   routingInstrumentation: Sentry.reactRouterV6Instrumentation(
          //     useEffect,
          //     useLocation,
          //     useNavigationType,
          //     createRoutesFromChildren,
          //     matchRoutes
          //   ),
          //   tracingOrigins: ["*"]
          // }),
          // new Sentry.Replay()
          // Add browser profiling integration to the list of integrations
          // new Sentry.BrowserProfilingIntegration()
        ],

        // Set tracesSampleRate to 1.0 to capture 100%
        // of transactions for performance monitoring.
        tracesSampleRate: 1.0,

        // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
        tracePropagationTargets: ['localhost', globalConfig.apiUrl],

        // Capture Replay for 10% of all sessions,
        // plus for 100% of sessions with an error
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0,
      });
    }

    ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
      <BrowserRouter>
        <Provider store={store}>
          <App />
        </Provider>
      </BrowserRouter>,
    );
  }
});

export async function setOriginToCookie(request: Request, origin: string) {
  const domain = await getDomain(request, origin);

  const serialized = await originCookie.serialize(origin, { domain });
  // log('setOriginInCookie:', serialized);
  return serialized;
}

export async function getDomain(request: Request, origin?: string) {
  if (isLocal(request.url)) {
    return 'localhost';
  }
  if (origin) {
    return new URL(origin).hostname;
  }

  // Parse cookie to get origin
  const cookieOrigin = await originCookie.parse(request.headers.get('cookie') || '');
  if (!cookieOrigin) {
    return undefined;
  }
  return new URL(cookieOrigin).hostname;
}

function isLocal(url: string): boolean {
  return new URL(url).hostname === 'localhost';
}
